import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Edit3, Calendar, Copy, AlertTriangle } from 'lucide-react';
import orgApi from '../api/organizations';
import ConfirmModal from '../components/UI/ConfirmModal';
import { useAuth } from '../auth/AuthProvider';

const INDUSTRIES = ['Technology', 'Manufacturing', 'Finance', 'Healthcare', 'Retail', 'Other'];

const OrgMembersOrg = ({ tenantId, onOrgDeleted }) => {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const successTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await orgApi.getOrg(tenantId);
      setOrg(data);
      setEditing({
        name: data.name,
        description: data.description || '',
        industry: data.industry || 'Technology',
      });
    } catch {
      setError('Failed to load organization.');
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => { if (successTimer.current) clearTimeout(successTimer.current); }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await orgApi.updateOrg(tenantId, editing);
      setSuccess('Organization saved.');
      await load();
      if (successTimer.current) clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await orgApi.deleteOrg(tenantId);
      localStorage.removeItem('tenant_id');
      setDeleteModal(false);

      // If user has other organizations, auto-switch to the first one
      if (response.remaining_organizations && response.remaining_organizations.length > 0) {
        const nextOrg = response.remaining_organizations[0];
        await orgApi.switchOrg(nextOrg.id);
        window.location.reload();
      } else {
        // No remaining organizations — user is still authenticated, just has no org
        window.location.reload();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to delete organization.');
    }
  };

  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  // user.role covers /auth/me responses that return a flat role string;
  // user.roles[0] covers the JWT-fallback shape { roles: [...] }.
  const displayRole = user?.role || user?.roles?.[0] || 'Org Admin';

  return (
    <div className="space-y-5">
      {success && (
        <div className="bg-emerald-400/10 border border-emerald-400/20 text-emerald-400 text-sm px-4 py-3 rounded-xl">
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-400/10 border border-red-400/20 text-red-400 text-sm px-4 py-3 rounded-xl">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-5">
        {/* Editable details */}
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Edit3 className="w-3.5 h-3.5 text-cyan-400" /> Organization Details
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Organization Name</label>
              <input
                value={editing.name || ''}
                onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Industry Sector</label>
              <select
                value={editing.industry || ''}
                onChange={e => setEditing(p => ({ ...p, industry: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors"
              >
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">
                Description <span className="text-gray-600">(optional)</span>
              </label>
              <input
                value={editing.description || ''}
                onChange={e => setEditing(p => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-500 transition-colors"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-5">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-cyan-400 text-gray-950 rounded-lg text-sm font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
            <button
              onClick={load}
              className="px-4 py-2 text-sm text-gray-400 border border-gray-700 rounded-lg hover:border-gray-500 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Read-only metadata */}
        <div className="bg-gray-900 border border-gray-700/50 rounded-xl p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-purple-400" /> Organization Metadata
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Tenant ID</label>
              <div className="flex gap-2">
                <div className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-xs text-gray-400 font-mono truncate">
                  {tenantId}
                </div>
                <button
                  onClick={() => copyToClipboard(tenantId)}
                  className="p-2 text-gray-500 hover:text-gray-300 border border-gray-700 rounded-lg transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Created</label>
              <div className="px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-xs text-gray-400">
                {org?.created_at
                  ? new Date(org.created_at).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur' }) + ' MYT'
                  : '—'}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Your Role</label>
              <div className="px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-xs text-purple-400 font-semibold">
                {displayRole}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1.5">Status</label>
              <div className="px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-xs flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-emerald-400 font-semibold">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Danger zone */}
      <div className="bg-red-400/5 border border-red-400/20 rounded-xl p-5">
        <p className="text-sm font-semibold text-red-400 flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4" /> Danger Zone · ORG-M-03
        </p>
        <p className="text-xs text-gray-500 mb-4 leading-relaxed">
          Permanently delete this organization and all associated data — assets, events, members, playbooks,
          and audit records. This action requires two-step confirmation and{' '}
          <strong className="text-gray-300">cannot be undone</strong>. A final{' '}
          <code className="bg-red-400/10 px-1 rounded text-red-400">ORG_PURGED</code> audit event will be
          emitted to the global immutable log per PDPA Section 10.
        </p>
        <button
          onClick={() => setDeleteModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-lg hover:bg-red-400/20 transition-colors"
        >
          <AlertTriangle className="w-3.5 h-3.5" /> Delete Organization
        </button>
      </div>

      <ConfirmModal
        isOpen={deleteModal}
        title="Delete Organization"
        danger
        confirmLabel="Confirm Delete"
        disabled={deleteConfirmText !== org?.name}
        onConfirm={handleDelete}
        onCancel={() => {
          setDeleteModal(false);
          setDeleteConfirmText('');
        }}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-400 mb-3">
            Type <strong className="text-white">{org?.name}</strong> to confirm:
          </p>
          <input
            value={deleteConfirmText}
            onChange={e => setDeleteConfirmText(e.target.value)}
            placeholder={org?.name}
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-red-500 transition-colors"
          />
        </div>
      </ConfirmModal>
    </div>
  );
};

export default OrgMembersOrg;
